/**
 * Частота замера: раз в неделю или раз в месяц.
 *
 * Стоит на обзоре бренда и на семантическом ядре — там, где человек смотрит на
 * числа и спрашивает себя, откуда они берутся. Настройка применяется сразу, без
 * кнопки «Сохранить»: выбор из двух значений с немедленным действием — это
 * переключатель, а не форма (app-kit.md §2).
 *
 * Настоящие радиокнопки в `fieldset`, а не сегменты-вкладки: это выбор одного
 * значения из двух, а не переключение вида экрана, и подпись группы тут обязана
 * быть видимой — «Раз в неделю» само по себе не говорит, о чём речь. Разметка та
 * же, что у выбора периода оплаты на «Тарифе».
 *
 * Рядом всегда стоит момент ближайшего замера. Без него «раз в месяц» — это
 * обещание без даты: клиент не знает, ждать ему замера завтра или через три
 * недели, и первым делом идёт спрашивать.
 *
 * Число месяца показывается только при месячной частоте: в недельной оно ничего
 * не задаёт, и мёртвое поле рядом с выключенным смыслом читается как поломка.
 * Числа больше 28 честно предупреждают о коротком месяце — правило «нет такого
 * числа, берём последний день» иначе выглядит как пропущенный замер.
 *
 * Кому это видно, решает не этот файл: маршрут расписания закрыт от клиента
 * агентства правилом доступа в контракте, и `reaches` спрашивает ту же таблицу.
 */

import { useState } from 'react';

import { api } from '../api/client';
import { t } from '../i18n';
import { useAccess } from '../session';
import { SelectField } from './fields';
import { useRead } from './query';
import { ErrorNotice } from './state';
import { momentProse } from '@shared/src/format';
import type { components } from '../api/schema';

type BrandSchedule = components['schemas']['BrandSchedule'];
type ScanCadence = components['schemas']['ScanCadence'];

/** Варианты частоты. Функция, а не таблица: подписи из каталога (B5). */
function cadenceOptions(): readonly { value: ScanCadence; label: string }[] {
  return [
    { value: 'weekly', label: t('cadence.weekly') },
    { value: 'monthly', label: t('cadence.monthly') },
  ];
}

/** Числа месяца. 29, 30 и 31 остаются в списке — короткий месяц их сдвигает. */
function dayOptions(): { value: string; label: string }[] {
  return Array.from({ length: 31 }, (_, index) => ({
    value: String(index + 1),
    label: t('cadence.day-option', { day: index + 1 }),
  }));
}

export function ScanCadencePicker({ brandId }: { brandId: string }) {
  const access = useAccess();
  const visible = access.reaches('GET', '/brands/{brandId}/schedule');
  const schedule = useRead<BrandSchedule>(visible ? `/brands/${brandId}/schedule` : null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  if (!visible) return null;
  // Ожидание молчит: частота — не то, ради чего человек открыл экран, и скелет
  // вместо неё уводил бы взгляд от чисел на полсекунды каждый раз.
  if (schedule.kind === 'loading') return null;
  // А вот отказ объясняется: «блока нет» и «блок не загрузился» — разные вещи,
  // и без этой строки человек читает второе как первое и ждёт частоту, которой
  // на экране больше не появится.
  if (schedule.kind === 'failed') return <ErrorNotice error={schedule.error} />;

  const current = schedule.data;
  const mayEdit = access.reaches('PUT', '/brands/{brandId}/schedule');
  const editable = mayEdit && !saving;

  // Роль без права правки видит частоту строкой, а не погашенным
  // переключателем: контрол, которым нельзя пользоваться, в этом кабинете не
  // показывается вовсе (app-kit.md §1).
  if (!mayEdit) {
    return (
      <p className="stamp">
        {t('cadence.readonly', {
          value:
            current.cadence === 'monthly'
              ? t('cadence.value.monthly')
              : t('cadence.value.weekly'),
          next: nextCensus(current).toLowerCase(),
        })}
      </p>
    );
  }

  const save = (changes: Partial<BrandSchedule>) => {
    setSaving(true);
    setFailure(null);
    api
      .put<BrandSchedule>(`/brands/${brandId}/schedule`, changes)
      .then((saved) => schedule.put(saved))
      .catch(setFailure)
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className="cadence">
        <div className="cadence-row">
          <fieldset className="cadence-kind">
            <legend>{t('cadence.legend')}</legend>
            <div className="seg-radio">
              {cadenceOptions().map((option) => (
                <label key={option.value}>
                  <input
                    type="radio"
                    name="scan-cadence"
                    value={option.value}
                    checked={current.cadence === option.value}
                    disabled={!editable}
                    onChange={() => save({ cadence: option.value })}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {current.cadence === 'monthly' ? (
            <SelectField
              label={t('cadence.day-label')}
              value={String(current.monthly_day)}
              options={dayOptions()}
              disabled={!editable}
              hint={current.monthly_day > 28 ? t('cadence.short-month') : ''}
              onChange={(day) => save({ monthly_day: Number(day) })}
            />
          ) : null}
        </div>
        <p className="hint" role="status">
          {nextCensus(current)}
        </p>
      </div>
      {failure ? <ErrorNotice error={failure} /> : null}
    </>
  );
}

/** Когда бренд померяется в следующий раз — словами, а не полем даты. */
function nextCensus(schedule: BrandSchedule): string {
  if (schedule.is_paused) return t('cadence.next.paused');
  if (!schedule.weekly_enabled) return t('cadence.next.disabled');
  // Пустой момент при включённом расписании значит «пора сейчас»: так заведён
  // бренд, которого планировщик ещё ни разу не брал.
  if (!schedule.next_weekly_at) return t('cadence.next.asap');
  return t('cadence.next.at', { when: momentProse(schedule.next_weekly_at) });
}
